import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useId, useState, type ReactNode } from 'react';

import { Button } from './Button';

interface CollapsibleCardProps {
  title: string;
  /** One line under the title, for what the section is. */
  description?: string;
  /** Shown at the right of the header - a status chip, a count, a warning. */
  meta?: ReactNode;
  /**
   * Controls for the section - a menu, an Edit button. Beside the fold button
   * rather than inside it, because a button inside a button is invalid and a
   * screen reader would read the two as one.
   */
  actions?: ReactNode;
  /** Controls before the title, outside the fold button - a drag handle. */
  leading?: ReactNode;
  /** The section is switched off: the title reads quieter. */
  muted?: boolean;
  /** An icon before the title, such as the kind of content a section holds. */
  icon?: ReactNode;
  /**
   * How the card folds: the whole header is the button (the default), or a
   * button of its own reading "Collapse" or "Expand" beside the actions, as
   * the ARC Builder's wireframe draws it. Either way it is
   * `<testId>-toggle-button` and says whether the body is open.
   */
  toggle?: 'header' | 'button';
  /** Open on first render. Ignored when `expanded` is supplied. */
  defaultExpanded?: boolean;
  /**
   * Controlled open state, for a page that needs to force a section open -
   * when a save inside it fails, for instance, and the message is in there.
   */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  children: ReactNode;
  /**
   * Names the card for the testing team's tests; the header button and the
   * body get `-toggle-button` and `-body`. See docs/test-ids.md.
   */
  testId: string;
}

/**
 * One section of a long page, behind a header you can fold away.
 *
 * The account page had two columns of cards and ran to several screens, with
 * the billing form alone twenty fields deep. Stacking the sections full width
 * and letting each fold means the page opens at a length somebody can take in,
 * and each section gets the whole width rather than two thirds of it.
 *
 * The body stays mounted when it is closed. A form that unmounted would throw
 * away whatever had been typed into it the moment somebody folded the section
 * to look at another one, which is the opposite of what folding is for.
 *
 * The header is a button inside a heading, which is the accordion pattern
 * screen readers expect: the heading is how somebody skims the page, the
 * button is how they open the bit they want.
 */
export function CollapsibleCard({
  title,
  description,
  meta,
  actions,
  leading,
  muted = false,
  icon,
  toggle: folds = 'header',
  defaultExpanded = false,
  expanded,
  onExpandedChange,
  children,
  testId,
}: CollapsibleCardProps) {
  const [uncontrolled, setUncontrolled] = useState(defaultExpanded);
  const bodyId = useId();
  const headerId = useId();

  const isOpen = expanded ?? uncontrolled;

  const toggle = () => {
    const next = !isOpen;
    setUncontrolled(next);
    onExpandedChange?.(next);
  };

  const titleText = (
    <Typography
      component="span"
      variant="subtitle1"
      color={muted ? 'text.secondary' : undefined}
      // A long name with no spaces breaks rather than pushing the
      // actions off the card (arc-and-discovery-hardening, B28).
      sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
    >
      {title}
    </Typography>
  );

  const metaBox = (chevron: boolean) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: chevron ? 1 : { xs: 1, sm: 2 },
        flexWrap: 'wrap',
        justifyContent: chevron ? 'flex-end' : 'flex-start',
        ml: chevron ? 'auto' : 0,
        // Kept inside the card: a chip wider than the room left wraps
        // its words rather than running under the card's actions.
        minWidth: 0,
        maxWidth: '100%',
        '& .MuiChip-root': { maxWidth: '100%', height: 'auto', minHeight: 24 },
        '& .MuiChip-label': { whiteSpace: 'normal' },
      }}
    >
      {meta}
      {chevron ? (
        <ExpandMoreIcon
          sx={{
            color: 'text.secondary',
            transition: 'transform 150ms',
            transform: isOpen ? 'rotate(180deg)' : 'none',
          }}
        />
      ) : null}
    </Box>
  );

  if (folds === 'button') {
    return (
      <Paper variant="outlined" data-testid={testId} sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            columnGap: { xs: 1, sm: 2 },
            rowGap: 1,
            px: { xs: 1, sm: 2 },
            py: 1.5,
          }}
        >
          {leading ? <Box sx={{ flexShrink: 0 }}>{leading}</Box> : null}
          {icon ? (
            <Box sx={{ display: 'flex', flexShrink: 0, color: 'text.primary' }}>
              {icon}
            </Box>
          ) : null}
          <Typography
            id={headerId}
            component="h2"
            sx={{ m: 0, flex: '1 1 8rem', minWidth: 0 }}
          >
            {titleText}
            {description ? (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                {description}
              </Typography>
            ) : null}
          </Typography>
          {meta ? (
            <Box sx={{ flex: '2 1 14rem', minWidth: 0 }}>{metaBox(false)}</Box>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={toggle}
              aria-expanded={isOpen}
              aria-controls={bodyId}
              data-testid={`${testId}-toggle-button`}
            >
              {isOpen ? 'Collapse' : 'Expand'}
            </Button>
            {actions}
          </Box>
        </Box>

        <Collapse in={isOpen}>
          <Box
            id={bodyId}
            role="region"
            aria-labelledby={headerId}
            data-testid={`${testId}-body`}
            sx={{
              px: { xs: 1.5, sm: 3 },
              pb: 3,
              pt: 0,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ pt: 2 }}>{children}</Box>
          </Box>
        </Collapse>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" data-testid={testId} sx={{ overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {leading ? <Box sx={{ pl: 1, flexShrink: 0 }}>{leading}</Box> : null}
        <Typography component="h2" sx={{ m: 0, flex: 1, minWidth: 0 }}>
          <ButtonBase
            id={headerId}
            onClick={toggle}
            aria-expanded={isOpen}
            aria-controls={bodyId}
            data-testid={`${testId}-toggle-button`}
            sx={{
              width: '100%',
              // Narrower on a phone: beside a card's handle and its actions, the
              // title otherwise had too little room for even a word such as
              // "Campaigns" (arc-and-discovery-hardening, B28).
              px: { xs: 1.5, sm: 3 },
              py: 2,
              gap: 2,
              display: 'flex',
              alignItems: 'center',
              textAlign: 'left',
              justifyContent: 'space-between',
              // On a narrow screen the meta drops under the title rather than
              // squeezing it until the two overlap.
              flexWrap: 'wrap',
              '&:focus-visible': { outline: '2px solid', outlineOffset: -2 },
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 10rem' }}>
              {titleText}
              {description ? (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  {description}
                </Typography>
              ) : null}
            </Box>

            {metaBox(true)}
          </ButtonBase>
        </Typography>
        {actions ? (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 2, flexShrink: 0 }}
          >
            {actions}
          </Box>
        ) : null}
      </Box>

      <Collapse in={isOpen}>
        <Box
          id={bodyId}
          role="region"
          aria-labelledby={headerId}
          data-testid={`${testId}-body`}
          sx={{ px: 3, pb: 3, pt: 0, borderTop: 1, borderColor: 'divider' }}
        >
          <Box sx={{ pt: 3 }}>{children}</Box>
        </Box>
      </Collapse>
    </Paper>
  );
}
